import Vue from 'vue'
import dynStorage from '@/utils/storage-dynamic'
import axios from '@/utils/axios-intents'
import { mapAddress } from '@/utils/address-helper';

let BROADBAND_FLOW_ID = 0
const BROADBAND_FLOW = 'broadband-flow'
// MOVE_HOUSE_FLOW constant removed - move house flow is now session-based only (no localStorage)

export const mapAddressItems = mapAddress

export function formatCartAddress(address) {
    if (!address) return '';
    
    let formattedAddress = '';
    if (address.Lines && address.Lines.length) {
        formattedAddress = address.Lines.filter(line => line).join(', ');
    }
    
    if (address.City) {
        formattedAddress += formattedAddress ? ', ' + address.City : address.City;
    }
    
    if (address.PostCode || address.Postcode) {
        formattedAddress += ' ' + (address.PostCode ?? address.Postcode);
    }
    
    return formattedAddress;
}

const state = {
    broadband_flow: {},
    move_house_flow: {},
}

const getters = {
    broadband_flow: state => state.broadband_flow,
    move_house_flow: state => state.move_house_flow,
}

const mutations = {
    saveBroadbandFlow(state, broadband_flow) {
        const { id, uuid, location, transfer, service, plan, with_wifi_router, rsf, broadband_device, serviceAvailability, contact_phone } = broadband_flow

        if (!id) {
            broadband_flow.id = `ufb-${ (new Date()).getTime() }${ ++BROADBAND_FLOW_ID }`
        }

        const stored_broadband_flow = {
            id: broadband_flow.id,
            uuid: uuid,
            location: {
                Formatted: location.Formatted,
                City: location.City,
                Country: location.Country,
                PostCode: location.PostCode ?? location.Postcode ?? '', 
                Postcode: location.PostCode ?? location.Postcode ?? '',  //backward compatibility
                Lines: location.Lines,
                LocationProvider: location.LocationProvider,
                LocationProviderIDX: location.LocationProviderIDX,
                Delivery: {
                    instructions: location.Delivery?.instructions,
                    address: location.Delivery?.address,
                    phone: location.Delivery?.phone,
                    picked_address_type: location.Delivery?.picked_address_type,
                }
            },
            delivery: broadband_flow.delivery || null,
            transfer: transfer,
            service: {
                ID: service?.ID ?? null,
                Plans: service?.Plans || []
            },
            plan: {
                ID: plan.ID,
                Name: plan.Name,
                Description: plan.Description,
                Price: plan.Price,
                ProductCatalogUUID: plan.ProductCatalogUUID,
                BroadbandProductType: plan.BroadbandProductType,
                OrderType: plan.OrderType,
                Attributes: ( plan.BroadbandProductType == 'UFB' ? plan.UFB_Attributes : plan.FWA_Attributes ) || []
            },
            with_wifi_router: with_wifi_router || false,
            rsf: rsf || {},
            broadband_type: plan.BroadbandProductType || 'UFB',
            broadband_device: broadband_device || {},
            serviceAvailability: serviceAvailability || null,
            contact_phone: contact_phone || null,
        }

        dynStorage.set(BROADBAND_FLOW, stored_broadband_flow, false, true)

        state.broadband_flow = broadband_flow
    },

    setBroadbandUUID(state, uuid) {
        if (uuid) {
            Vue.set(state.broadband_flow, 'uuid', uuid)
        } else {
            Vue.delete(state.broadband_flow, 'uuid')
        }
    },

    setBroadbandModemInfo(state, modem) {
        if (modem && Object.keys(modem).length) {
            Vue.set(state.broadband_flow, 'modem', {
                ID: modem.UUID,
                Name: modem.Name,
                PriceCents: modem.PriceCents || 0,
            })
        } else {
            Vue.delete(state.broadband_flow, 'modem')
        }
    },

    loadBroadbandFlow() {
        state.broadband_flow = dynStorage.get(BROADBAND_FLOW, true) || {}
    },

    resetBroadbandFlow() {
        state.broadband_flow = {}
        dynStorage.remove(BROADBAND_FLOW)
    },

    saveMoveHouseFlow(state, move_house_flow) {
        const stored_move_house_flow = {
            currentStep: move_house_flow.currentStep || 0,
            newAddress: move_house_flow.newAddress || null,
            selectedPlan: move_house_flow.selectedPlan || null,
            moveInDate: move_house_flow.moveInDate || null,
            availablePlans: move_house_flow.availablePlans || [],
            serviceAvailability: move_house_flow.serviceAvailability || null,
            isSubmitted: move_house_flow.isSubmitted || false,  // Track submission status
            submittedAt: move_house_flow.submittedAt || null,   // Track when submitted
        }

        // Session-based only - NO localStorage persistence
        state.move_house_flow = stored_move_house_flow
    },

    loadMoveHouseFlow(state) {
        // Session-based only - always start with empty state
        state.move_house_flow = {}
    },

    resetMoveHouseFlow(state) {
        // Session-based only - clear Vuex state only
        state.move_house_flow = {}
    },
}

const actions = {
    loadBroadbandModemInfo({getters, commit, dispatch}, broadband_flow) {
        if (broadband_flow.broadband_type == 'UFB') { 
            const modem = broadband_flow.broadband_device && Object.keys(broadband_flow.broadband_device).length > 0
                ? {
                    UUID: broadband_flow.broadband_device.UUID,
                    Name: broadband_flow.broadband_device.Name,
                    PriceCents: broadband_flow.broadband_device.Price || 0,
                }
                : null;
            
            commit('setBroadbandModemInfo', modem);
            commit('saveBroadbandFlow', getters.broadband_flow);
            return Promise.resolve(getters.broadband_flow.modem);
        }
        
        if (broadband_flow.broadband_type == 'FWA') { 
            return Promise.resolve(null)
        }
    },

    broadbandSearchAddress({}, payload) {
        return axios.get('/broadband/v1/search_address', { params: payload })
            .then(({data}) => Promise.resolve(data))
    },

    broadbandGetPlansCatalog({getters}, { Location, ProductType }) {
        const payload = {
            LocationProvider: Location.LocationProvider,
            LocationProviderIDX: Location.LocationProviderIDX,
            ProductType: ProductType
        }

        return axios.get('/broadband/v1/plans', { params: payload })
            .then(({data}) => {
                const noServiceMessage = 'No Service Information Available'

                if (data.ServiceAvailability && data.ServiceAvailability.Status) {
                    const Status = data?.ServiceAvailability?.Status ?? null

                    if (Status == 'Available') {
                        return Promise.resolve({ ...data})
                    } else {
                        return Promise.reject(new Error(noServiceMessage))
                    }
                } else {
                    return Promise.reject(new Error(noServiceMessage))
                }
            }).catch(error => {
                return Promise.reject(error)
            })
        },

        
}

export default {
    state,
    getters,
    mutations,
    actions,
}
